import React from "react";

// Full-screen "Stand By" style takeover. Owner posts an announcement; every
// viewer who hasn't acked THIS specific notice_id sees the card with SMPTE
// bars + scanlines behind it and a single CTA to dismiss. After clicking,
// the ack is written to localStorage and they fall through to the CRT.
//
// Visual language is intentionally the same as MaintenanceOverlay — bars,
// scanlines, chromatic title, JetBrains Mono — so the announcement reads as
// part of the same broadcast control system.
export default function NoticeOverlay({ title, body, ctaLabel, validUntil, onAcknowledge }) {
  const safeTitle = (title || "BROADCAST NOTICE").trim();
  const safeBody = (body || "").trim();
  const safeCta = (ctaLabel || "CONTINUE").trim();

  let validText = null;
  if (validUntil) {
    try {
      const d = new Date(validUntil);
      if (Number.isFinite(d.getTime())) {
        validText = d.toLocaleString([], {
          year: "numeric", month: "short", day: "2-digit",
          hour: "2-digit", minute: "2-digit",
        });
      }
    } catch (e) { /* fall through — no banner */ }
  }

  return (
    <div
      className="absolute inset-0 z-[50] bg-black text-[var(--phosphor)] flex flex-col items-center justify-center overflow-hidden py-8"
      style={{ fontFamily: "JetBrains Mono, monospace" }}
      data-testid="notice-overlay"
    >
      {/* SMPTE colour-bar accent strip at the top — short flash of broadcast
          test pattern to sell the "official announcement" feel. */}
      <div className="absolute top-0 left-0 right-0 h-3 flex">
        {["#C7C700", "#00C7C7", "#00C700", "#C700C7", "#C70000", "#0000C7", "#FFFFFF"].map((c) => (
          <div key={c} style={{ background: c, flex: 1 }} />
        ))}
      </div>
      {/* Scanlines + vignette */}
      <div
        className="absolute inset-0 pointer-events-none opacity-40"
        style={{
          background:
            "repeating-linear-gradient(0deg, color-mix(in srgb, var(--phosphor) 6%, transparent) 0 1px, transparent 1px 3px)",
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.92) 100%)",
        }}
      />

      <div className="relative w-full max-w-3xl max-h-full flex flex-col text-center px-6 min-h-0">
        <div className="text-[10px] md:text-xs tracking-[0.5em] opacity-70 mb-3 shrink-0">
          ┌─ SCARTLEAD.TV BROADCAST CONTROL ─┐
        </div>
        <div
          className="text-xl md:text-3xl tracking-[0.12em] chromatic mb-4 phosphor-glow break-words shrink-0"
          data-testid="notice-title"
        >
          {safeTitle.toUpperCase()}
        </div>

        {/* Scrollable body section — long announcements no longer push the
            header off the top of the CRT or the CONTINUE button off the
            bottom. The `flex-1 min-h-0` pair is the standard "let me
            shrink, please" handshake with the flex parent. */}
        <div className="flex-1 min-h-0 overflow-y-auto scrollbar-crt">
          <div
            className="text-sm md:text-base leading-relaxed opacity-95 whitespace-pre-wrap text-left mx-auto max-w-2xl border-l-2 border-[color-mix(in_srgb,var(--phosphor)_50%,transparent)] pl-4"
            data-testid="notice-body"
          >
            {safeBody}
          </div>

          {validText && (
            <div className="mt-4 text-[10px] tracking-[0.3em] opacity-60">
              VALID UNTIL {validText.toUpperCase()}
            </div>
          )}
        </div>

        <button
          onClick={onAcknowledge}
          data-testid="notice-acknowledge"
          autoFocus
          className="mt-6 mx-auto inline-block px-10 py-3 border-2 border-[var(--phosphor)] text-[var(--phosphor)] tracking-[0.3em] text-sm md:text-base hover:bg-[var(--phosphor)] hover:text-black transition-colors phosphor-glow font-bold shrink-0"
        >
          ▶ {safeCta}
        </button>

        <div className="mt-3 text-[10px] opacity-50 tracking-[0.3em] shrink-0">
          PRESS ENTER OR CLICK TO DISMISS
        </div>
      </div>

      <div className="absolute bottom-3 left-4 text-[10px] opacity-50 tracking-widest">
        CRT-1986
      </div>
      <div className="absolute bottom-3 right-4 text-[10px] opacity-50 tracking-widest">
        SCARTLEAD.TV
      </div>
    </div>
  );
}
